import { PersonOff } from '@mui/icons-material';
import { AutocompleteRenderGroupParams, Box } from '@mui/material';

import { InputState } from '../../utils/inputState';
import { ReturnComponent } from '../../utils/reactTypes';
import {
    AxleAutoCompleteFieldProps,
    AxleAutocompleteField,
} from '../formFields/AxleAutocompleteField';
import { HorizontalStack } from '../utils/linearLayouts';
import { IClinicianAndVisitCount, SELECTOR_GROUP } from './LiveView';

const ClinicianWithVisitCountOptionLabel = ({
    option,
}: {
    option: IClinicianAndVisitCount | null;
}): ReturnComponent => {
    if (!option) {
        return (
            <Box display='flex'>
                <PersonOff /> <Box ml={1}>Unassigned</Box>
            </Box>
        );
    }
    return (
        <HorizontalStack sx={{ justifyContent: 'space-between' }}>
            <Box>{`${option.clinician.firstName} ${option.clinician.lastName}`.trim()}</Box>
            <Box>{option.visitCount}</Box>
        </HorizontalStack>
    );
};

export const LiveViewClinicianSelector = ({
    inputState,
    options,
    ...rest
}: {
    inputState: InputState<IClinicianAndVisitCount | null>;
    options: IClinicianAndVisitCount[];
} & Omit<
    AxleAutoCompleteFieldProps<string | null, IClinicianAndVisitCount>,
    'inputState' | 'options' | 'optionToValue' | 'optionToLabel' | 'renderOption'
>): ReturnComponent => {
    return (
        <AxleAutocompleteField<IClinicianAndVisitCount, IClinicianAndVisitCount | null>
            {...rest}
            enabled={true}
            sortOptions={false}
            inputState={inputState}
            options={options}
            handleNullValue={() => {
                inputState.setInputState(null);
            }}
            placeholder='Select a clinician'
            optionStyle={{
                '&.MuiAutocomplete-option.Mui-focused': {
                    backgroundColor: '#26A69A',
                    color: '#FFFFFF',
                },
            }}
            optionToValue={(clinician) => clinician}
            optionToLabel={(clinician) =>
                `${clinician.clinician.firstName} ${clinician.clinician.lastName}`.trim()
            }
            renderOption={(clinician) => <ClinicianWithVisitCountOptionLabel option={clinician} />}
            displayMissingOptionValue={true}
            renderGroup={({ key, children, group }: AutocompleteRenderGroupParams) => {
                const isUnassigned = group === SELECTOR_GROUP.UNASSIGNED;
                return (
                    <Box key={key}>
                        <HorizontalStack
                            borderBottom={'1px solid #D9D9D9'}
                            py={!isUnassigned ? '6px' : undefined}
                            px={!isUnassigned ? 2 : undefined}
                            justifyContent='space-between'
                        >
                            {!isUnassigned ? (
                                <>
                                    <span>Clinician</span>
                                    <span>Visits</span>
                                </>
                            ) : null}
                        </HorizontalStack>
                        <Box color={isUnassigned ? '#B3B3B3' : '#000000'}>{children}</Box>
                    </Box>
                );
            }}
        />
    );
};
